import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaClock } from 'react-icons/fa';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';

const ResolveModal = ({ isOpen, onClose, supportRequest }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !supportRequest) return null;

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await updateDoc(doc(db, 'supportRequests', supportRequest.id), {
        approvedByUser: true,
        status: 'resolved',
      });
      toast.success('Request resolved successfully');
      onClose();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Failed to approve request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    try {
      await updateDoc(doc(db, 'supportRequests', supportRequest.id), {
        approvedByUser: false,
        status: 'in-progress',
      });
      toast.info('Request sent back to support team');
      onClose();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to reject request');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    // Handle Firebase Timestamp
    if (date.toDate) {
      date = date.toDate();
    }
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-90"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Regarding: {supportRequest.subject}
                </h3>
                
                <div className="mt-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Your original message ({formatDate(supportRequest.createdAt)}):
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-4">
                    {supportRequest.message}
                  </p>
                  
                  <div className="border-t dark:border-gray-600 pt-4 mt-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Our response:
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-white dark:bg-gray-800 p-3 rounded-md border dark:border-gray-600">
                      {supportRequest.resolveMessageFromAdmin}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4">
                    {supportRequest.status === 'resolved' ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            This case is solved, you can contact us again at <a href="mailto:cashsule.app@gmail.com" className="text-primary-600 dark:text-primary-400">cashsule.app@gmail.com</a>.
                        </p>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Is this response helpful? If not, you can reject it and contact us directly at <a href="mailto:cashsule.app@gmail.com" className="text-primary-600 dark:text-primary-400">cashsule.app@gmail.com</a>.
                        </p>
                    )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {supportRequest.status === 'resolved' ? (
              <div className="w-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                <FaCheckCircle className="text-green-500 mr-2" />
                This request was resolved {supportRequest.updatedAt ? `on ${formatDate(supportRequest.updatedAt)}` : ''}
              </div>
            ) : supportRequest.approvedByUser === false ? (
              <div className="w-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                <FaClock className="text-yellow-500 mr-2" />
                Request sent back to support team.
              </div>
            ) : (
              <>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleApprove}
                  className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isSubmitting ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : (
                    <FaCheckCircle className="mr-2" />
                  )}
                  This resolves my issue
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleReject}
                  className="mt-3 w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-700 text-base font-medium text-white hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isSubmitting ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : (
                    <FaTimesCircle className="mr-2" />
                  )}
                  I need more help
                </button>
              </>
            )}
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResolveModal;
